import $ from 'jquery'
import { Util } from 'bootstrap'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'fonts'
const DATA_KEY = `opp.${NAME}`
const JQUERY_NO_CONFLICT = $.fn[NAME]

const Default = {
  mode: 'tabbed'
}

const DefaultType = {
  mode: 'string'
}

const ClassName = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  FONTS_INIT: 'fonts-initialized'
}

const Selector = {
  DATA_INIT: '[data-init="fonts"]',
  FONTS_DATA: '.fonts-data',
  FONTS_INPUT: '.fonts-input',
  FONTS_LIST: '.fonts-list',
  FONTS_LIST_ITEM: '.fonts-list-item',
  FONTS_LIST_ITEM_ACTIVE: '.fonts-list-item.active',
  FONTS_LIST_ITEM_TEXT: ':not(img).fonts-list-item-text',
  FONTS_LIST_ITEM_TEXT_IMG: 'img.fonts-list-item-text',
  FONTS_PANE: '.fonts-pane',
  FONTS_PANE_TEMPLATE: '.fonts-pane-template',
  FONTS_TAB: '.fonts-tab',
  FONTS_TAB_ACTIVE: '.fonts-tab.active',
  FONTS_TAB_NOT_DISABLED: '.fonts-tab:not(.disabled)'
}

const Event = {
  BS_TAB_SHOW: 'show.bs.tab'
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Fonts {
  constructor(element, config) {
    this._element = element
    this._document = element.ownerDocument
    this._config = this._getConfig(config)
    this._tabbed = this._config.mode === 'tabbed'
    this._fonts = this._getFonts()
    this._inputElem = this._getInputElem()

    // Cache initial font for comparison.
    this._initialFontId = this._getActiveFontId()

    // Handle show event triggered by letter tab or font list-group-item.
    $(this._element).on(Event.BS_TAB_SHOW, evt => this._onTabShow(evt))

    if (this._tabbed) {
      this._fontTabElems = this._getFontTabElems()
      this._fontPaneElems = this._getFontPaneElems()
      this._fontTabElemIndex = this._indexElementsByTabName(this._fontTabElems)
      this._fontPaneElemIndex = this._indexElementsByTabName(this._fontPaneElems)

      // Initialize all tabs.
      for (const tabElem of this._fontTabElems) {
        this._initTab(tabElem)
      }
    } else {
      this._listElem = this._getListElem()
      this._initList(this._listElem, this._fonts.all)
    }
  }

  // Public

  show() {
    if (this._tabbed) {
      const font = this._getActiveFont()
      const tabName = this._tabNameForFont(font)
      const tabElem = this._fontTabElemIndex[tabName]
      $(tabElem || this._getDefaultTab()).tab('show')
    }
  }

  font() {
    const font = this._getActiveFont()
    if (font) {
      return { ...font }
    }
  }

  load(id, text, target) {
    this._load(this._fonts.idIndex[id], text, target)
  }

  // Private

  _getConfig(config) {
    if (config && config.mode) {
      config.mode = config.mode.trim()
    }

    config = {
      ...Default,
      ...config
    }
    Util.typeCheckConfig(NAME, config, DefaultType)
    return config
  }

  _getActiveFont() {
    const id = this._getActiveFontId()
    return this._fonts.idIndex[id]
  }

  _getActiveFontId() {
    const val = $(this._inputElem).val().replace(/\s/g, '')
    if (val) {
      return parseInt(val, 10)
    }
  }

  _getFonts() {
    const el = this._element.querySelector(Selector.FONTS_DATA)
    const all = (el && el.text && JSON.parse(el.text)) || []

    // Sort by name (in-place).
    all.sort((a, b) => {
      const sortA = a.name.toLowerCase() // ignore case
      const sortB = b.name.toLowerCase() // ignore case
      if (sortA < sortB) {
        return -1
      }

      if (sortA > sortB) {
        return 1
      }

      // names must be equal
      return 0
    })

    const idIndex = {}
    const letterIndex = this._tabbed && []

    for (const font of all) {
      // Index by id (as integer).
      idIndex[font.id] = font

      // Index by first letter of name (optionally).
      if (letterIndex) {
        let k = this._tabNameForFont(font)
        if (k.match(/[^a-z]/i)) {
          k = 'num'
        }

        (letterIndex[k] = letterIndex[k] || []).push(font)
      }
    }

    const letters = letterIndex && Object.keys(letterIndex).sort()

    return { all, idIndex, letterIndex, letters }
  }

  _getInputElem() {
    return $(this._element).find(Selector.FONTS_INPUT)
  }

  _getListElem(elem) {
    const _elem = $(elem)[0] || this._element
    return _elem.querySelector(Selector.FONTS_LIST)
  }

  _getFontTabElems() {
    return $(this._element).find(Selector.FONTS_TAB)
  }

  _getFontPaneElems() {
    const tplNode = this._element.querySelector(Selector.FONTS_PANE_TEMPLATE)
    if (tplNode) {
      const tpl = $(tplNode).text()
      const parent = tplNode.parentNode
      for (const tabName of this._fonts.letters) {
        $(tpl.replace(/#{tabName}/g, tabName)).appendTo(parent)
      }
    }

    return $(this._element).find(Selector.FONTS_PANE)
  }

  _getDefaultTab() {
    return this._element.querySelector(Selector.FONTS_TAB_ACTIVE) || this._element.querySelector(Selector.FONTS_TAB_NOT_DISABLED)
  }

  _tabNameForFont(font) {
    if (font && font.name && font.name[0]) {
      return font.name[0].toLowerCase()
    }
  }

  _tabNameForElement(elem) {
    return $(elem).data('tab-name')
  }

  _indexElementsByTabName(elems) {
    const index = {}

    for (const elem of elems) {
      const tabName = this._tabNameForElement(elem)
      if (tabName) {
        index[tabName] = elem
      }
    }

    return index
  }

  _initElem(elem) {
    const $elem = $(elem)
    if ($elem.hasClass(ClassName.FONTS_INIT)) {
      return
    }

    $elem.addClass(ClassName.FONTS_INIT)

    return $elem
  }

  _getElemFontList(elem) {
    return this._fonts.letterIndex[this._tabNameForElement(elem)]
  }

  _initTab(tabElem) {
    const $tab = this._initElem(tabElem)
    if (!$tab) {
      return
    }

    const list = this._getElemFontList($tab)

    if (list && list.length > 0) {
      $tab.removeClass(ClassName.DISABLED)
    } else {
      $tab.addClass(ClassName.DISABLED)
    }
  }

  _initPane(paneElem) {
    const $pane = this._initElem(paneElem)
    if (!$pane) {
      return
    }

    this._initList(
      this._getListElem($pane),
      this._getElemFontList($pane)
    )
  }

  _initList(listElem, fontList) {
    const $list = this._initElem(listElem)
    if (!$list) {
      return
    }

    if (!(fontList && fontList.length > 0)) {
      return
    }

    const $itemTemplate = $list.find(Selector.FONTS_LIST_ITEM).detach()

    // Create document fragments to group DOM updates.
    const headFrag = this._document.createDocumentFragment()
    const listFrag = this._document.createDocumentFragment()

    for (const font of fontList) {
      // Clone item for each font.
      const $item = $itemTemplate.clone()

      // Update item text element if present.
      const $text = $item.find(Selector.FONTS_LIST_ITEM_TEXT)
      if ($text.length > 0) {
        // Load webfont for text preview, if needed.
        this._load(font, font.name, headFrag)
        // Update
        $text.css(Fonts.styleProps(font)).text(font.name)
      }

      // Update item img element if present.
      $item.find(Selector.FONTS_LIST_ITEM_TEXT_IMG).attr({
        title: font.name, src: font.preview_url
      })

      // Add font id to data attribute for later reference.
      $item.attr('data-font-id', font.id)

      // Add active class if current font is active.
      if (font.id === this._getActiveFontId()) {
        $item.addClass(ClassName.ACTIVE)
      }

      // Finally, append item to list fragment.
      $(listFrag).append($item)
    }

    // Append fragments to list and head elements.
    $(this._document.head).append(headFrag)
    $list.append(listFrag)
  }

  _activateTab(elem) {
    // Clear active list items in other tabs.
    $(this._fontPaneElems).find(Selector.FONTS_LIST_ITEM_ACTIVE).not(elem).removeClass(ClassName.ACTIVE)

    // Update input value.
    $(this._inputElem).val($(elem).data('font-id'))

    // Trigger change event, including font data.
    $(this._inputElem).trigger('change', [this.font(), this._initialFontId])
  }

  _initPaneForTab(tabElem) {
    const tabName = this._tabNameForElement(tabElem)
    const paneElem = this._fontPaneElemIndex[tabName]
    this._initPane(paneElem)
  }

  _onTabShow(evt) {
    const $tab = $(evt.target)

    if ($tab.is(Selector.FONTS_LIST_ITEM)) {
      this._activateTab($tab)
    } else {
      this._initPaneForTab($tab)
    }

    return true
  }

  _load(font, text, targetNode) {
    $(this._document).fontloader('load', font, text, targetNode)
  }

  // Static

  static cssText(font) {
    const rules = [`font-family: ${font.family}`]
    if (font.weight) {
      rules.push(`font-weight: ${font.weight}`)
    }

    if (font.style) {
      rules.push(`font-style: ${font.style}`)
    }

    return rules.join('; ')
  }

  static styleProps(font) {
    const props = { fontFamily: `${font.family}` }
    if (font.weight) {
      props.fontWeight = `${font.weight}`
    }

    if (font.style) {
      props.fontStyle = `${font.style}`
    }

    return props
  }

  static _jQueryInterface(config, ...args) {
    return this.each(function () {
      let data = $(this).data(DATA_KEY)
      const _config = {
        ...Default,
        ...$(this).data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new Fonts(this, _config)
        $(this).data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }

        data[config](...args)
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
 */

Fonts._jQueryInterface.call($(Selector.DATA_INIT), 'show')

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 */

$.fn[NAME] = Fonts._jQueryInterface
$.fn[NAME].Constructor = Fonts
$.fn[NAME].noConflict = () => {
  $.fn[NAME] = JQUERY_NO_CONFLICT
  return Fonts._jQueryInterface
}

export default Fonts
